// Pure helpers for the composer: the format the canvas implies, filters as adjustment presets,
// undo and redo over versions, and the generation step list.
import type { CardEdit, PostFormat } from '@str/shared';

export type Adjust = CardEdit['adjust'];

/** One photo is a picture, several are a carousel, a lone video is a reel. */
export function formatFor(cards: { kind: 'photo' | 'video' }[]): PostFormat {
  if (cards.length === 1 && cards[0]?.kind === 'video') return 'Reel';
  return cards.length > 1 ? 'Carousel' : 'Image';
}

export function formatLabel(format: PostFormat, count: number): string {
  if (format === 'Reel' || format === 'Story') return format;
  return count === 1 ? '1 picture' : `${count} pictures`;
}

export type LookKey = 'none' | 'warm' | 'crisp' | 'film';
export const LOOKS: { key: LookKey; label: string; adjust: Adjust }[] = [
  { key: 'none', label: 'None', adjust: { brightness: 0, contrast: 0, saturation: 0, warmth: 0 } },
  {
    key: 'warm',
    label: 'Warm',
    adjust: { brightness: 0.04, contrast: 0.04, saturation: 0.15, warmth: 0.2 },
  },
  {
    key: 'crisp',
    label: 'Crisp',
    adjust: { brightness: 0.02, contrast: 0.14, saturation: 0.08, warmth: 0 },
  },
  {
    key: 'film',
    label: 'Film',
    adjust: { brightness: 0.05, contrast: -0.08, saturation: -0.18, warmth: 0.1 },
  },
];
/** The auto-fix: a little more light and colour, never a different scene. */
export const AUTO_FIX: Adjust = { brightness: 0.08, contrast: 0.06, saturation: 0.1, warmth: 0.05 };

const near = (a: number, b: number) => Math.abs(a - b) < 0.005;
export function lookOf(adjust: Adjust): LookKey | null {
  const hit = LOOKS.find((l) =>
    (Object.keys(l.adjust) as (keyof Adjust)[]).every((k) => near(l.adjust[k], adjust[k])),
  );
  return hit?.key ?? null;
}

type VersionLike = { id: string; n: number; parentVersionId: string | null; note: string };

export function undoTarget(versions: VersionLike[], currentId: string | null): string | null {
  const current = versions.find((v) => v.id === currentId);
  if (!current) return null;
  if (current.parentVersionId && versions.some((v) => v.id === current.parentVersionId))
    return current.parentVersionId;
  const earlier = versions.filter((v) => v.n < current.n).sort((a, b) => b.n - a.n);
  return earlier[0]?.id ?? null;
}

export function redoTarget(versions: VersionLike[], currentId: string | null): string | null {
  const children = versions
    .filter((v) => v.parentVersionId === currentId)
    .sort((a, b) => b.n - a.n);
  return children[0]?.id ?? null;
}

export type Step = { label: string; state: 'todo' | 'doing' | 'done' };
export function stepsFrom(statusTexts: string[], finished: boolean): Step[] {
  const seen: string[] = [];
  for (const t of statusTexts) {
    const label = t.replace(/[…\s]+$/, '').trim();
    if (label && seen[seen.length - 1] !== label) seen.push(label);
  }
  return seen.map((label, i) => ({
    label,
    state: finished || i < seen.length - 1 ? 'done' : 'doing',
  }));
}

export function parseHashtag(input: string): string {
  return input
    .trim()
    .replace(/^#/, '')
    .replace(/[^A-Za-z0-9_]/g, '');
}
